/**
 * Hook to fetch active note templates (Questionnaires) from Medplum.
 *
 * Uses the shared documentation settings resolver internally so that
 * Settings → Documentation controls which templates appear here.
 * Appointment default selection uses HealthcareService note-template-id
 * (see healthcareServiceNoteTemplate.ts), not template service-type tags.
 */
import { useMemo } from 'react';
import type { Questionnaire } from '@medplum/fhirtypes';
import { useResolvedTemplates } from '../../settings/template-library/hooks';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NoteTemplateOption {
  id: string;
  title: string;
  questionnaire: Questionnaire;
}

interface UseNoteTemplatesOptions {
  /** When true, do not query Medplum (caller uses local/mock templates instead). */
  skip?: boolean;
}

interface UseNoteTemplatesResult {
  templates: NoteTemplateOption[];
  loading: boolean;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useNoteTemplates(options: UseNoteTemplatesOptions = {}): UseNoteTemplatesResult {
  const { skip = false } = options;

  const { templates: resolved, loading, error } = useResolvedTemplates({
    target: 'appointment-note',
    skip,
  });

  const templates: NoteTemplateOption[] = useMemo(
    () =>
      resolved
        .filter((q) => q.id)
        .map((q) => ({
          id: q.id!,
          title: q.title ?? q.name ?? 'Untitled Template',
          questionnaire: q,
        })),
    [resolved],
  );

  return { templates, loading, error };
}
